import React, { useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { SearchBar } from "./SearchBar";
import { HoldingsTable } from "./HoldingsTable";
import { Badge } from "@/components/ui/badge";
import { useAtomValue } from "jotai";
import { SelectedFundAtom } from "@/store/FundStateAtom";
import { HoldingWithInstrumentAndAccount } from "@/types";

interface HoldingsCardProps {
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  isLoading: boolean;
  filteredHoldings: HoldingWithInstrumentAndAccount[];
  onViewHolding: (holding: HoldingWithInstrumentAndAccount) => void;
  onConfirmHolding: (
    holding: HoldingWithInstrumentAndAccount,
    isConfirmed: boolean
  ) => void;
  onDeleteHolding: (holding: HoldingWithInstrumentAndAccount) => void;
}

export const HoldingsCard = ({
  searchQuery,
  setSearchQuery,
  isLoading,
  filteredHoldings,
  onViewHolding,
  onConfirmHolding,
  onDeleteHolding,
}: HoldingsCardProps) => {
  const selectedFund = useAtomValue(SelectedFundAtom);

  // Calculate total NAV (sum of amounts) grouped by currency
  const currencyTotals = useMemo(() => {
    return filteredHoldings.reduce((totals, holding) => {
      const currency = holding.currency || "USD";
      if (!totals[currency]) {
        totals[currency] = 0;
      }
      totals[currency] += holding.baseAmount;
      return totals;
    }, {} as Record<string, number>);
  }, [filteredHoldings]);

  // Format currency
  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  return (
    <Card className="mb-6">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <div className="flex items-center gap-3">
          <CardTitle>Portfolio Holdings</CardTitle>
          {selectedFund && (
            <Badge variant="outline" className="text-sm">
              Filtered for {selectedFund.name}
            </Badge>
          )}
        </div>
        <div className="flex flex-col items-end">
          <span className="text-sm text-muted-foreground mb-1">Total NAV</span>
          <div className="flex flex-wrap gap-2 justify-end">
            {Object.entries(currencyTotals).map(([currency, total]) => (
              <Badge key={currency} variant="secondary" className="py-1 px-3">
                <span className="font-semibold text-base">
                  {formatCurrency(total, currency)}
                </span>
              </Badge>
            ))}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <HoldingsTable
          isLoading={isLoading}
          holdings={filteredHoldings}
          onViewHolding={onViewHolding}
          onConfirmHolding={onConfirmHolding}
          onDeleteHolding={onDeleteHolding}
        />
      </CardContent>
    </Card>
  );
};
